import React, { useState, useEffect } from 'react';
import { Button, Form, Row, Col, Card, Spinner, Modal, Table, Badge } from 'react-bootstrap';
import { MdClose, MdAdd, MdDelete, MdArrowBack, MdEdit } from 'react-icons/md';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import CustomToast from 'components/toast/customToast';
import { useAuth } from 'components/authentication/signup-login/AuthContext';
import { 
  createPalette, 
  listPalettes, 
  updatePalette, 
  deletePalette 
} from 'store/auth';

// Default static palettes - NOT stored in DB, always visible to all users
const DEFAULT_PALETTES = [
    // {
    //   id: 'default-1',
    //   palette_name: 'Purple Mist',
    //   map_colors: ['#f2e6ff', '#d9b3ff', '#b266ff', '#7a1fa2', '#4a0072'],
    //   contrast_colors: [
    //     '#b266ff', // base color
    //     '#ff80ab',
    //     '#ce93d8',
    //     '#9575cd',
    //     '#ff8a65',
    //     '#4fc3f7',
    //     '#81c784',
    //     '#ffb74d',
    //     '#90a4ae',
    //     '#212121'
    //   ],
    //   isDefault: true
    // },
    // {
    //   id: 'default-2',
    //   palette_name: 'Clay Earth',
    //   map_colors: ['#f5e6cc', '#e0b97d', '#c97b2f', '#8b4513', '#5c2e0f'],
    //   contrast_colors: [
    //     '#c97b2f', // base color
    //     '#ff7043',
    //     '#ffb74d',
    //     '#a1887f',
    //     '#81c784',
    //     '#64b5f6',
    //     '#9575cd',
    //     '#90a4ae',
    //     '#f06292',
    //     '#212121'
    //   ],
    //   isDefault: true
    // }
  ];
  

const CustomColorPalette = () => {
    const navigate = useNavigate();
    const { user, isAuthenticated } = useAuth();

    // State for the page
    const [paletteName, setPaletteName] = useState('');
    const [baseColor, setBaseColor] = useState('#3498db');
    const [generatedColors, setGeneratedColors] = useState([]);
    const [contrastColors, setContrastColors] = useState(['#3498db', '#000000', '#FF0000', '#00FF00', '#0000FF', '#FFFF00', '#FF00FF', '#00FFFF', '#FFA500', '#808080']);
    const [isValid, setIsValid] = useState(false);
    const [savedPalettes, setSavedPalettes] = useState([]);
    const [editingPalette, setEditingPalette] = useState(null);
    const [isEditMode, setIsEditMode] = useState(false);
    const [isLoading, setIsLoading] = useState(true);
    const [showModal, setShowModal] = useState(false);
    const [isSaving, setIsSaving] = useState(false);

    // Redirect to login if not authenticated
    useEffect(() => {
        if (!isAuthenticated) {
            navigate('/');
        } else {
            setIsLoading(false);
            loadPalettes();
        }
    }, [isAuthenticated, navigate]);

    // Load palettes from API (user-created only)
    const loadPalettes = async () => {
        try {
            setIsLoading(true);
            const response = await listPalettes();
            setSavedPalettes(response.data.palettes || []);
        } catch (error) {
            console.error('Error loading palettes:', error);
            toast(<CustomToast message="Failed to load palettes" type="error" />, { position: "top-right" });
        } finally {
            setIsLoading(false);
        }
    };

    // Combine default palettes with user palettes for display
    const getAllPalettes = () => {
        return [...DEFAULT_PALETTES, ...savedPalettes];
    };

    // Generate 5 color variations from base color
    const generateColorPalette = (baseColor) => {
        const colors = [];
        
        const hex = baseColor.replace('#', '');
        const r = parseInt(hex.substr(0, 2), 16);
        const g = parseInt(hex.substr(2, 2), 16);
        const b = parseInt(hex.substr(4, 2), 16);

        const variations = [
            { blend: 0.5, darker: false },
            { blend: 0.25, darker: false },
            { blend: 0, darker: false },
            { blend: 0.3, darker: true },
            { blend: 0.6, darker: true }
        ];

        variations.forEach(({ blend, darker }) => {
            let newR, newG, newB;
            
            if (!darker && blend > 0) {
                newR = Math.round(r + (255 - r) * blend);
                newG = Math.round(g + (255 - g) * blend);
                newB = Math.round(b + (255 - b) * blend);
            } else if (darker) {
                newR = Math.round(r * (1 - blend));
                newG = Math.round(g * (1 - blend));
                newB = Math.round(b * (1 - blend));
            } else {
                newR = r;
                newG = g;
                newB = b;
            }

            newR = Math.max(0, Math.min(255, newR));
            newG = Math.max(0, Math.min(255, newG));
            newB = Math.max(0, Math.min(255, newB));

            const hexR = newR.toString(16).padStart(2, '0');
            const hexG = newG.toString(16).padStart(2, '0');
            const hexB = newB.toString(16).padStart(2, '0');
            
            colors.push(`#${hexR}${hexG}${hexB}`);
        });

        return colors;
    };

    // Convert RGB to HSL
    const rgbToHsl = (r, g, b) => {
        r /= 255;
        g /= 255;
        b /= 255;
        const max = Math.max(r, g, b);
        const min = Math.min(r, g, b);
        let h, s, l = (max + min) / 2;

        if (max === min) {
            h = s = 0;
        } else {
            const d = max - min;
            s = l > 0.5 ? d / (2 - max - min) : d / (max + min);
            switch (max) {
                case r: h = (g - b) / d + (g < b ? 6 : 0); break;
                case g: h = (b - r) / d + 2; break;
                case b: h = (r - g) / d + 4; break;
            }
            h /= 6;
        }
        return [h * 360, s * 100, l * 100];
    };

    // Convert HSL to RGB
    const hslToRgb = (h, s, l) => {
        h /= 360;
        s /= 100;
        l /= 100;
        const hue2rgb = (p, q, t) => {
            if (t < 0) t += 1;
            if (t > 1) t -= 1;
            if (t < 1/6) return p + (q - p) * 6 * t;
            if (t < 1/2) return q;
            if (t < 2/3) return p + (q - p) * (2/3 - t) * 6;
            return p;
        };
        let r, g, b;
        if (s === 0) {
            r = g = b = l;
        } else {
            const q = l < 0.5 ? l * (1 + s) : l + s - l * s;
            const p = 2 * l - q;
            r = hue2rgb(p, q, h + 1/3);
            g = hue2rgb(p, q, h);
            b = hue2rgb(p, q, h - 1/3);
        }
        return [Math.round(r * 255), Math.round(g * 255), Math.round(b * 255)];
    };

    // Convert RGB to hex
    const rgbToHex = (r, g, b) => {
        const hexR = r.toString(16).padStart(2, '0');
        const hexG = g.toString(16).padStart(2, '0');
        const hexB = b.toString(16).padStart(2, '0');
        return `#${hexR}${hexG}${hexB}`;
    };

    // Generate intelligent contrast color suggestions
    const generateContrastSuggestions = (baseColor) => {
        const hex = baseColor.replace('#', '');
        const r = parseInt(hex.substr(0, 2), 16);
        const g = parseInt(hex.substr(2, 2), 16);
        const b = parseInt(hex.substr(4, 2), 16);
        
        const [h, s, l] = rgbToHsl(r, g, b);
        
        const suggestions = [];
        
        suggestions.push(baseColor);
        
        const complementaryH = (h + 180) % 360;
        const [compR, compG, compB] = hslToRgb(complementaryH, s, l);
        suggestions.push(rgbToHex(compR, compG, compB));
        
        const triadicH1 = (h + 120) % 360;
        const triadicH2 = (h + 240) % 360;
        const [triR1, triG1, triB1] = hslToRgb(triadicH1, s, l);
        const [triR2, triG2, triB2] = hslToRgb(triadicH2, s, l);
        suggestions.push(rgbToHex(triR1, triG1, triB1));
        suggestions.push(rgbToHex(triR2, triG2, triB2));
        
        const analogousH1 = (h + 30) % 360;
        const analogousH2 = (h - 30 + 360) % 360;
        const [anaR1, anaG1, anaB1] = hslToRgb(analogousH1, s, l);
        const [anaR2, anaG2, anaB2] = hslToRgb(analogousH2, s, l);
        suggestions.push(rgbToHex(anaR1, anaG1, anaB1));
        suggestions.push(rgbToHex(anaR2, anaG2, anaB2));
        
        const splitComp1 = (h + 150) % 360;
        const splitComp2 = (h + 210) % 360;
        const [sc1R, sc1G, sc1B] = hslToRgb(splitComp1, s, l);
        const [sc2R, sc2G, sc2B] = hslToRgb(splitComp2, s, l);
        suggestions.push(rgbToHex(sc1R, sc1G, sc1B));
        suggestions.push(rgbToHex(sc2R, sc2G, sc2B));
        
        suggestions.push('#000000');
        suggestions.push('#808080');
        
        return suggestions;
    };

    // Update generated colors when base color changes
    useEffect(() => {
        const colors = generateColorPalette(baseColor);
        setGeneratedColors(colors);
        
        const newContrastColors = [...contrastColors];
        newContrastColors[0] = baseColor;
        setContrastColors(newContrastColors);
    }, [baseColor]);

    // Apply contrast color suggestions
    const applyContrastSuggestions = () => {
        const suggestions = generateContrastSuggestions(baseColor);
        setContrastColors(suggestions);
        toast(<CustomToast message="Contrast colors updated with intelligent suggestions!" type="success" />, { position: "top-right" });
    };

    // Validate form
    useEffect(() => {
        const isNameValid = paletteName.trim() !== '' && paletteName.trim().length <= 8;
        const isPaletteUnique = !savedPalettes.some(p => {
            const existingName = p.palette_data?.palette_name || '';
            return existingName.toLowerCase() === paletteName.trim().toLowerCase() && 
                   (!isEditMode || p.id !== editingPalette?.id);
        });
        // Also check against default palette names
        const notDefaultName = !DEFAULT_PALETTES.some(p => 
            p.palette_name.toLowerCase() === paletteName.trim().toLowerCase()
        );
        setIsValid(isNameValid && isPaletteUnique && notDefaultName && generatedColors.length === 5 && contrastColors.length === 10);
    }, [paletteName, generatedColors, contrastColors, savedPalettes, isEditMode, editingPalette]);

    // Handle contrast color change
    const handleContrastColorChange = (index, color) => {
        const newColors = [...contrastColors];
        newColors[index] = color;
        setContrastColors(newColors);
    };

    // Handle palette name change
    const handlePaletteNameChange = (e) => {
        const value = e.target.value;
        if (value.length <= 8) {
            setPaletteName(value);
        }
    };

    // Check if palette name is unique
    const isPaletteNameUnique = () => {
        const uniqueAmongUsers = !savedPalettes.some(p => {
            const existingName = p.palette_data?.palette_name || '';
            return existingName.toLowerCase() === paletteName.trim().toLowerCase() && 
                   (!isEditMode || p.id !== editingPalette?.id);
        });
        const notDefaultName = !DEFAULT_PALETTES.some(p => 
            p.palette_name.toLowerCase() === paletteName.trim().toLowerCase()
        );
        return uniqueAmongUsers && notDefaultName;
    };

    // Handle save
    const handleSave = async () => {
        if (contrastColors.length !== 10) {
            toast(<CustomToast message="Please select exactly 10 contrast colors" type="error" />, { position: "top-right" });
            return;
        }

        if (paletteName.trim().length > 8) {
            toast(<CustomToast message="Palette name must be 8 characters or less" type="error" />, { position: "top-right" });
            return;
        }

        if (!isPaletteNameUnique()) {
            toast(<CustomToast message="Palette name must be unique" type="error" />, { position: "top-right" });
            return;
        }

        if (!isValid) {
            return;
        }

        try {
            setIsSaving(true);

            const paletteData = {
                palette_name: paletteName.trim(),
                map_colors: generatedColors,
                contrast_colors: contrastColors
            };

            if (isEditMode && editingPalette) {
                await updatePalette(editingPalette.id, paletteData);
                toast(<CustomToast message="Palette updated successfully!" type="success" />, { position: "top-right" });
            } else {
                await createPalette(paletteData);
                toast(<CustomToast message="Palette saved successfully!" type="success" />, { position: "top-right" });
            }

            await loadPalettes();
            handleCloseModal();
        } catch (error) {
            console.error('Error saving palette:', error);
            toast(<CustomToast message={error.message || "Failed to save palette"} type="error" />, { position: "top-right" });
        } finally {
            setIsSaving(false);
        }
    };

    // Delete saved palette (only for user-created palettes)
    const handleDeletePalette = async (id) => {
        if (!window.confirm('Are you sure you want to delete this palette?')) {
            return;
        }

        try {
            await deletePalette(id);
            toast(<CustomToast message="Palette deleted successfully!" type="success" />, { position: "top-right" });
            await loadPalettes();
        } catch (error) {
            console.error('Error deleting palette:', error);
            toast(<CustomToast message="Failed to delete palette" type="error" />, { position: "top-right" });
        }
    };

    // Edit palette (only for user-created palettes)
    const handleEditPalette = (palette) => {
        if (!palette || (!palette.palette_data && !palette.isDefault)) {
            console.error('Invalid palette data');
            return;
        }

        setEditingPalette(palette);
        setIsEditMode(true);
        
        if (palette.isDefault) {
            setPaletteName(palette.palette_name || '');
            const mapColors = palette.map_colors || [];
            if (mapColors.length > 0) {
                const baseColorIndex = Math.floor(mapColors.length / 2);
                setBaseColor(mapColors[baseColorIndex]);
            }
            setContrastColors(palette.contrast_colors || []);
        } else {
            setPaletteName(palette.palette_data.palette_name || '');
            const mapColors = palette.palette_data.map_colors || [];
            if (mapColors.length > 0) {
                const baseColorIndex = Math.floor(mapColors.length / 2);
                setBaseColor(mapColors[baseColorIndex]);
            }
            setContrastColors(palette.palette_data.contrast_colors || []);
        }
        
        setShowModal(true);
    };

    // Open modal for new palette
    const handleOpenModal = () => {
        handleReset();
        setShowModal(true);
    };

    // Close modal
    const handleCloseModal = () => {
        setShowModal(false);
        handleReset();
    };

    // Cancel edit
    const handleCancelEdit = () => {
        setIsEditMode(false);
        setEditingPalette(null);
        handleCloseModal();
    };

    // Reset form
    const handleReset = () => {
        setPaletteName('');
        setBaseColor('#3498db');
        setContrastColors(['#3498db', '#000000', '#FF0000', '#00FF00', '#0000FF', '#FFFF00', '#FF00FF', '#00FFFF', '#FFA500', '#808080']);
        setIsEditMode(false);
        setEditingPalette(null);
    };

    // Show loading spinner
    if (isLoading || !isAuthenticated || !user) {
        return (
            <div className="d-flex justify-content-center align-items-center" style={{ height: '70vh' }}>
                <Spinner animation="border" role="status" className="me-2" />
                <span>Loading...</span>
            </div>
        );
    }

    const allPalettes = getAllPalettes();

    return (
        <div className="container my-5">
            <div className="row">
                <div className="col-12">
                    <Card>
                        <Card.Header className="d-flex justify-content-between align-items-center">
                            <h5 className="mb-0">Custom Color Palette Creator</h5>
                            <Button 
                                variant="primary"
                                size="sm"
                                onClick={handleOpenModal}
                            >
                                <MdAdd className="me-1" /> Add Color Palette
                            </Button>
                        </Card.Header>
                        <Card.Body>
                            {allPalettes.length === 0 ? (
                                <p className="text-muted text-center">No palettes available.</p>
                            ) : (
                                <Table striped bordered hover responsive>
                                    <thead>
                                        <tr>
                                            <th style={{ width: '80px' }}>S.No</th>
                                            <th>Palette Name</th>
                                            <th>Chart Colors</th>
                                            <th>Map Colors</th>
                                            <th style={{ width: '120px' }}>Actions</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {allPalettes.map((palette, index) => {
                                            const isDefault = palette.isDefault === true;
                                            const paletteData = isDefault ? palette : palette.palette_data;
                                            
                                            if (!paletteData) {
                                                return null;
                                            }
                                            
                                            return (
                                                <tr key={palette.id}>
                                                    <td className="text-center">{index + 1}</td>
                                                    <td>
                                                        <strong>{paletteData.palette_name || 'Unnamed'}</strong>
                                                        {isDefault && (
                                                            <Badge bg="info" className="ms-2">Default</Badge>
                                                        )}
                                                    </td>
                                                    <td>
                                                        <div className="d-flex flex-wrap gap-1">
                                                            {(paletteData.contrast_colors || []).map((color, colorIndex) => (
                                                                <div
                                                                    key={colorIndex}
                                                                    className="border rounded"
                                                                    style={{
                                                                        width: '30px',
                                                                        height: '30px',
                                                                        backgroundColor: color,
                                                                        flex: '0 0 auto'
                                                                    }}
                                                                    title={color}
                                                                />
                                                            ))}
                                                        </div>
                                                    </td>
                                                    <td>
                                                        <div className="d-flex flex-wrap gap-1">
                                                            {(paletteData.map_colors || []).map((color, colorIndex) => (
                                                                <div
                                                                    key={colorIndex}
                                                                    className="border rounded"
                                                                    style={{
                                                                        width: '30px',
                                                                        height: '30px',
                                                                        backgroundColor: color,
                                                                        flex: '0 0 auto'
                                                                    }}
                                                                    title={color}
                                                                />
                                                            ))}
                                                        </div>
                                                    </td>
                                                    <td className="text-center">
                                                        {!isDefault ? (
                                                            <>
                                                                <Button
                                                                    variant="link"
                                                                    size="sm"
                                                                    className="text-primary p-1"
                                                                    onClick={() => handleEditPalette(palette)}
                                                                    title="Edit Palette"
                                                                >
                                                                    <MdEdit style={{ fontSize: '20px' }} />
                                                                </Button>
                                                                <Button
                                                                    variant="link"
                                                                    size="sm"
                                                                    className="text-danger p-1"
                                                                    onClick={() => handleDeletePalette(palette.id)}
                                                                    title="Delete Palette"
                                                                >
                                                                    <MdDelete style={{ fontSize: '20px' }} />
                                                                </Button>
                                                            </>
                                                        ) : (
                                                            <span className="text-muted small">—</span>
                                                        )}
                                                    </td>
                                                </tr>
                                            );
                                        })}
                                    </tbody>
                                </Table>
                            )}
                        </Card.Body>
                    </Card>
                </div>
            </div>

            {/* Modal for Create/Edit Palette */}
            <Modal
                show={showModal}
                onHide={handleCloseModal}
                size="xl"
                centered
                backdrop="static"
            >
                <Modal.Header closeButton>
                    <Modal.Title>
                        {isEditMode && editingPalette
                            ? `Edit Palette: ${editingPalette.isDefault ? editingPalette.palette_name : editingPalette.palette_data?.palette_name}`
                            : "Create New Palette"}
                    </Modal.Title>
                </Modal.Header>

                <Modal.Body style={{ maxHeight: "none" }}>
                    <Form>
                        <Form.Group className="mb-3">
                            <Form.Label>
                                Palette Name <span className="text-danger">*</span>
                            </Form.Label>
                            <Form.Control
                                type="text"
                                placeholder="Enter palette name (max 8 characters)"
                                value={paletteName}
                                onChange={handlePaletteNameChange}
                                maxLength={8}
                                isInvalid={paletteName.trim() !== "" && !isPaletteNameUnique()}
                            />
                            <Form.Control.Feedback type="invalid">
                                Palette name must be unique and cannot match default palette names
                            </Form.Control.Feedback>
                        </Form.Group>

                        <Row>
                            <Col md={8}>
                                <Card className="mb-3">
                                    <Card.Header>
                                        <h6 className="mb-0">Generated Base Colors (5 colors)</h6>
                                    </Card.Header>
                                    <Card.Body>
                                        <div className="alert alert-info mb-3" style={{ fontSize: "14px" }}>
                                            <strong>Note:</strong> Select the base color to generate 5 color variations.
                                        </div>

                                        <Row className="align-items-center mb-3">
                                            <Col md={4}>
                                                <Form.Group>
                                                    <Form.Label>Base Color</Form.Label>
                                                    <Form.Control
                                                        type="color"
                                                        value={baseColor}
                                                        onChange={(e) => setBaseColor(e.target.value)}
                                                        style={{ height: "35px", width: "80px" }}
                                                    />
                                                </Form.Group>
                                            </Col>
                                            <Col md={8}>
                                                <Form.Label>Generated Palette</Form.Label>
                                                <div className="d-flex gap-2 flex-wrap">
                                                    {generatedColors.map((color, i) => (
                                                        <div
                                                            key={i}
                                                            className="border rounded"
                                                            style={{
                                                                width: "30px",
                                                                height: "30px",
                                                                backgroundColor: color,
                                                            }}
                                                            title={color}
                                                        />
                                                    ))}
                                                </div>
                                            </Col>
                                        </Row>
                                    </Card.Body>
                                </Card>

                                <Card>
                                    <Card.Header>
                                        <div className="d-flex justify-content-between align-items-center">
                                            <h6 className="mb-0">Chart Colors (10 colors)</h6>
                                            <Button
                                                variant="outline-primary"
                                                size="sm"
                                                onClick={applyContrastSuggestions}
                                                title="Generate intelligent color suggestions based on base color"
                                            >
                                                🎨 Suggest Colors
                                            </Button>
                                        </div>
                                    </Card.Header>
                                    <Card.Body>
                                        <Row>
                                            {contrastColors.map((color, index) => (
                                                <Col md={2} sm={3} xs={4} key={index} className="mb-2">
                                                    <div className="d-flex flex-column align-items-center gap-1">
                                                        <Form.Control
                                                            type="color"
                                                            value={color}
                                                            onChange={(e) => handleContrastColorChange(index, e.target.value)}
                                                            style={{ width: "40px", height: "35px" }}
                                                            disabled={index === 0}
                                                            title={index === 0 ? "First color is always the base color" : ""}
                                                        />
                                                        <small className="text-muted" style={{ fontSize: "11px" }}>
                                                            {index === 0 ? "Base" : `C${index + 1}`}
                                                        </small>
                                                    </div>
                                                </Col>
                                            ))}
                                        </Row>

                                        <div className="alert alert-info mt-3 mb-0" style={{ fontSize: "14px" }}>
                                            <strong>Note:</strong> Select colors by clicking the color box or click "Suggest Colors" for intelligent recommendations.
                                        </div>
                                    </Card.Body>
                                </Card>
                            </Col>

                            <Col md={4}>
                                <Card className="h-100 d-flex flex-column justify-content-between">
                                    <Card.Header>
                                        <h6 className="mb-0">Color Preview</h6>
                                    </Card.Header>
                                    <Card.Body>
                                        <div>
                                            <h6>Base Colors</h6>
                                            <div className="d-flex gap-2 flex-wrap mb-3">
                                                {generatedColors.map((color, i) => (
                                                    <div
                                                        key={i}
                                                        className="border rounded"
                                                        style={{
                                                            width: "28px",
                                                            height: "28px",
                                                            backgroundColor: color,
                                                        }}
                                                        title={color}
                                                    />
                                                ))}
                                            </div>

                                            <h6>Chart Colors</h6>
                                            <div className="d-flex gap-2 flex-wrap">
                                                {contrastColors.map((color, i) => (
                                                    <div
                                                        key={i}
                                                        className="border rounded"
                                                        style={{
                                                            width: "28px",
                                                            height: "28px",
                                                            backgroundColor: color,
                                                        }}
                                                        title={color}
                                                    />
                                                ))}
                                            </div>
                                        </div>
                                    </Card.Body>

                                    <div className="d-flex justify-content-end gap-2 p-3 border-top">
                                        <Button
                                            variant="secondary"
                                            onClick={isEditMode ? handleCancelEdit : handleCloseModal}
                                            disabled={isSaving}
                                        >
                                            Cancel
                                        </Button>
                                        <Button
                                            variant="primary"
                                            onClick={handleSave}
                                            disabled={!isValid || isSaving}
                                        >
                                            {isSaving ? (
                                                <>
                                                    <Spinner animation="border" size="sm" className="me-1" />
                                                    Saving...
                                                </>
                                            ) : (
                                                isEditMode ? "Update Palette" : "Save Palette"
                                            )}
                                        </Button>
                                    </div>
                                </Card>
                            </Col>
                        </Row>
                    </Form>
                </Modal.Body>
            </Modal>
        </div>
    );
};

export default CustomColorPalette;